import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useGroups, useConfig, useVehicleCatalog, useWorkshopServiceAdvisors } from "../lib/api";
import VehiclePicker from "../components/VehiclePicker";
import { EMPTY_VEHICLE, vehicleChoiceError, vehiclePayload } from "../lib/vehicles";
import { useAuth } from "../lib/AuthContext";
import { useTenantTerms } from "../lib/TenantConfigContext";
import { useBookingUploads } from "../lib/useBookingUploads";
import { useBookingFlow } from "../lib/useBookingFlow";
import UploadSlot from "../components/booking/UploadSlot";
import BookingProgress from "../components/booking/BookingProgress";
import {
  BookingLayout,
  FORM_CARD,
  FormField,
  GroupPreview,
  NOTE,
  PLATE,
  PhoneInput,
  ROW2,
  ReviewList,
  STEP_BODY,
  StepSidebar,
  WizardFoot,
  bookingMembers,
  deliveryLabel,
  inputCls,
  vehiclePreview,
} from "../components/booking/WizardParts";
import { Btn, cx } from "../components/msd/ui";
import {
  isValidDeliveryTimeline,
  isValidDeliveryTime,
  isDeliveryPromiseInPast,
  toPromisedDeliveryISO,
  DEFAULT_DELIVERY_TIME,
} from "./CreateGroup";
import Dropdown from "../components/Dropdown";

function deliveryTimelineBounds() {
  const today = new Date();
  const max = new Date(today);
  max.setFullYear(today.getFullYear() + 5);
  const iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { min: iso(today), max: iso(max) };
}

const STEPS = [
  { key: "vehicle", label: "Vehicle Details", subtitle: "Registration number" },
  { key: "customer", label: "Customer Info", subtitle: "Name & phone number" },
  { key: "workshop", label: "Workshop & Delivery", subtitle: "Location, advisor & ETA" },
  { key: "ro_card", label: "RO Card", subtitle: "Repair order document" },
  { key: "ro_estimate", label: "RO Estimate", subtitle: "Estimate document" },
  { key: "create", label: "Create Group", subtitle: "Review & create" },
];
// A booking document: PDF unless the brand chose a photo or a video for that message.
const roFiles = (name) => ({
  document: { label: `${name} PDF`, kinds: "PDF files", accept: "application/pdf" },
  image: { label: `${name} photo`, kinds: "images", accept: "image/*" },
  video: { label: `${name} video`, kinds: "videos", accept: "video/*" },
});
const RO_CARD_FILES = roFiles("RO Card");
const RO_ESTIMATE_FILES = roFiles("RO Estimate");
const SLOTS = { roCard: { stage: "job_card" }, roEstimate: { stage: "estimate" } };

export default function HyundaiBookService() {
  const { me } = useAuth();
  const { roleLabel, stageFiles } = useTenantTerms();
  // The RO card and estimate are the Job Card and Estimate messages' files.
  const roCardFile = RO_CARD_FILES[stageFiles.job_card] || RO_CARD_FILES.document;
  const roEstimateFile = RO_ESTIMATE_FILES[stageFiles.estimate] || RO_ESTIMATE_FILES.document;
  const { data: cfg } = useConfig();
  const { data: advisorsData } = useWorkshopServiceAdvisors();
  // The same cached list the model picker reads; here it gives the preview its photo.
  const { data: catalog } = useVehicleCatalog();
  useGroups();
  const flow = useBookingFlow();

  const isServiceAdvisor = me?.role === "sa";
  const isSingleLocationLocked = me?.role && ["sa", "ccm", "wm", "psf", "receptionist"].includes(me.role);
  const isGm = me?.role === "gm";
  const userWorkshopLocation = me?.workshop_location;
  const userServiceAdvisorId = me?.service_advisor_id;
  const userName = me?.name;
  const userPhone = me?.phone;
  const gmLocations = useMemo(
    () => (Array.isArray(me?.workshop_locations) ? me.workshop_locations.filter(Boolean) : []),
    [me]
  );
  const allConfigLocations = (cfg?.rows || []).map((r) => r["Workshop Location"]).filter(Boolean);
  const locations = isGm
    ? gmLocations
    : isSingleLocationLocked && userWorkshopLocation
      ? [userWorkshopLocation]
      : allConfigLocations;

  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    group_name: "",
    customer_name: "",
    customer_number: "",
    workshop_location: isSingleLocationLocked ? userWorkshopLocation || "" : "",
    service_advisor_id: isServiceAdvisor ? String(userServiceAdvisorId || "") : "",
    service_advisor_name: isServiceAdvisor ? userName || "" : "",
    service_advisor_number: isServiceAdvisor ? userPhone || "" : "",
    delivery_date: "",
    delivery_time: "",
  });
  const [vehicle, setVehicle] = useState(EMPTY_VEHICLE);
  // Names only decide how an uploaded file is called, so the form-so-far is enough.
  const namesRef = useRef({});
  namesRef.current = { customerName: f.customer_name.trim(), vehicle: f.group_name.trim() };
  const getNames = useCallback(() => namesRef.current, []);
  const uploads = useBookingUploads({ slots: SLOTS, getNames });

  useEffect(() => {
    const updates = {};
    if (isSingleLocationLocked && userWorkshopLocation && !f.workshop_location)
      updates.workshop_location = userWorkshopLocation;
    if (isGm && gmLocations.length === 1 && !f.workshop_location)
      updates.workshop_location = gmLocations[0];
    if (isServiceAdvisor) {
      if (userServiceAdvisorId && !f.service_advisor_id) updates.service_advisor_id = String(userServiceAdvisorId);
      if (userName && !f.service_advisor_name) updates.service_advisor_name = userName;
      if (userPhone && !f.service_advisor_number) updates.service_advisor_number = userPhone;
    }
    if (Object.keys(updates).length) setF((s) => ({ ...s, ...updates }));
  }, [isSingleLocationLocked, isGm, isServiceAdvisor, userWorkshopLocation, gmLocations, userServiceAdvisorId, userName, userPhone, f.workshop_location, f.service_advisor_id, f.service_advisor_name, f.service_advisor_number]);

  const advisorsForLocation = (advisorsData?.rows || []).filter(
    (a) => a.workshop_location === f.workshop_location
  );

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const setLocation = (e) => {
    const workshop_location = e.target.value;
    setF((s) => ({ ...s, workshop_location, service_advisor_id: "", service_advisor_name: "", service_advisor_number: "" }));
  };
  const setAdvisor = (e) => {
    const id = e.target.value;
    const advisor = advisorsForLocation.find((a) => String(a.id) === id);
    setF((s) => ({
      ...s,
      service_advisor_id: id,
      service_advisor_name: advisor?.name || "",
      service_advisor_number: advisor?.number || "",
    }));
  };
  const setPhoneDigits = (e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
    setF((s) => ({ ...s, customer_number: digits }));
  };

  const canNext = () => {
    if (step === 0) return !!f.group_name.trim() && !vehicleChoiceError(vehicle);
    if (step === 1) return !!f.customer_name.trim() && f.customer_number.length === 10;
    // The advisor is required. Without one nobody is invited to the group on
    // their behalf and the job card never appears in their desk — this step
    // silently produced exactly that.
    if (step === 2)
      return (
        !!f.workshop_location &&
        !!f.service_advisor_number &&
        isValidDeliveryTimeline(f.delivery_date) &&
        isValidDeliveryTime(f.delivery_time)
      );
    // The RO documents upload as they are picked; continue once they are stored.
    if (step === 3) return uploads.urls("roCard").length > 0 && !uploads.busy && !uploads.failed("roCard");
    if (step === 4) return uploads.urls("roEstimate").length > 0 && !uploads.busy && !uploads.failed("roEstimate");
    return true;
  };

  const next = () => {
    if (step === 2 && isDeliveryPromiseInPast(f.delivery_date, f.delivery_time)) {
      toast.error("That delivery time has already passed. Pick a later one.");
      return;
    }
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const back = () => { if (step > 0) setStep(step - 1); };

  const submit = () => {
    flow.start({
      payload: {
        group_name: f.group_name.trim(),
        description: "",
        customer_name: f.customer_name.trim(),
        customer_number: `+91${f.customer_number}`,
        workshop_location: f.workshop_location,
        service_advisor_name: f.service_advisor_name,
        service_advisor_number: f.service_advisor_number,
        service_type: "Estimate Approve",
        delivery_timeline: f.delivery_date,
        promised_delivery_at: toPromisedDeliveryISO(f.delivery_date, f.delivery_time),
        ro_card_url: uploads.urls("roCard")[0],
        ro_estimate_url: uploads.urls("roEstimate")[0],
        ...vehiclePayload(vehicle),
      },
    });
  };

  const { min, max } = deliveryTimelineBounds();
  const isLastStep = step === STEPS.length - 1;
  const creating = flow.active;

  const nextLabel = () => {
    if (step === 2) return "RO Estimate Approve";
    if (isLastStep) return "Create group";
    return "Continue";
  };

  const picture = vehiclePreview(vehicle, catalog?.rows);
  const roFile = (slot) => uploads.items(slot)[0]?.name;

  return (
    <BookingLayout
      sub="Creates the customer's WhatsApp service group"
      preview={
        <GroupPreview
          image={picture.image}
          name={f.group_name.trim()}
          sub={picture.sub}
          meta={[
            ["Workshop", f.workshop_location || "Not chosen"],
            ["RO Card", roFile("roCard") || "Not added"],
            ["Delivery", deliveryLabel(f.delivery_date, f.delivery_time)],
          ]}
          members={bookingMembers({
            customerName: f.customer_name.trim(),
            customerPhone: f.customer_number.length === 10 ? `+91 ${f.customer_number}` : "",
            advisorName: f.service_advisor_name || f.service_advisor_number,
            advisorRole: roleLabel("sa"),
            hasWorkshop: !!f.workshop_location,
          })}
        />
      }
    >
      <section className={FORM_CARD}>
        <StepSidebar steps={STEPS} currentStep={step} completedUpTo={creating ? STEPS.length - 1 : step} />

        <div className={STEP_BODY}>
          {step === 0 && (
            <>
              <FormField label="Vehicle number" required hint="Becomes the WhatsApp group name.">
                <input
                  data-testid="input-group-name"
                  autoFocus
                  className={cx(inputCls, PLATE)}
                  value={f.group_name}
                  onChange={set("group_name")}
                  placeholder="e.g., MH12AB1234"
                />
              </FormField>
              <FormField group label="Model" required>
                <VehiclePicker value={vehicle} onChange={setVehicle} inputClassName={inputCls} hint="Its photo becomes the group picture." />
              </FormField>
            </>
          )}

          {step === 1 && (
            <>
              <FormField label="Customer name" required>
                <input
                  data-testid="input-customer-name"
                  autoFocus
                  className={inputCls}
                  value={f.customer_name}
                  onChange={set("customer_name")}
                  placeholder="Full name"
                />
              </FormField>
              <FormField label="WhatsApp number" required hint="The customer gets the group invite on this number.">
                <PhoneInput
                  data-testid="input-customer-number"
                  placeholder="10-digit mobile number"
                  inputMode="numeric"
                  value={f.customer_number}
                  onChange={setPhoneDigits}
                />
              </FormField>
            </>
          )}

          {step === 2 && (
            <>
              <FormField label="Workshop location" required>
                <Dropdown
                  data-testid="select-location"
                  className={inputCls}
                  value={f.workshop_location}
                  onChange={setLocation}
                  disabled={isSingleLocationLocked}
                >
                  <option value="">Select location…</option>
                  {locations.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </Dropdown>
              </FormField>
              <FormField label={roleLabel("sa")}>
                {isServiceAdvisor ? (
                  <input
                    data-testid="input-service-advisor"
                    className={inputCls}
                    value={f.service_advisor_name || f.service_advisor_number || "You"}
                    disabled
                  />
                ) : (
                  <Dropdown
                    data-testid="select-service-advisor"
                    className={inputCls}
                    value={f.service_advisor_id}
                    onChange={setAdvisor}
                    disabled={!f.workshop_location}
                  >
                    <option value="">
                      {f.workshop_location ? "Select service advisor…" : "Select a workshop location first"}
                    </option>
                    {advisorsForLocation.map((a) => (
                      <option key={a.id} value={a.id}>{a.name || a.number}{a.number ? ` (${a.number})` : ""}</option>
                    ))}
                  </Dropdown>
                )}
              </FormField>
              <FormField group label="Promised delivery" required hint="When you've committed the vehicle will be ready">
                <div className={ROW2}>
                  <input
                    type="date"
                    data-testid="input-delivery-timeline"
                    className={inputCls}
                    min={min}
                    max={max}
                    value={f.delivery_date}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (!next || isValidDeliveryTimeline(next)) {
                        setF((s) => ({
                          ...s,
                          delivery_date: next,
                          delivery_time: next ? s.delivery_time || DEFAULT_DELIVERY_TIME : "",
                        }));
                      }
                    }}
                  />
                  <input
                    type="time"
                    step={1800}
                    data-testid="input-delivery-time"
                    aria-label="Delivery time"
                    className={inputCls}
                    value={f.delivery_time}
                    onChange={set("delivery_time")}
                  />
                </div>
              </FormField>
            </>
          )}

          {step === 3 && (
            <UploadSlot
              label={roCardFile.label}
              kinds={roCardFile.kinds}
              accept={roCardFile.accept}
              required
              items={uploads.items("roCard")}
              onPick={(files) => uploads.pick("roCard", files)}
              onRemove={(id) => uploads.remove("roCard", id)}
              onRetry={(id) => uploads.retry("roCard", id)}
              testid="booking-ro-card"
            />
          )}

          {step === 4 && (
            <UploadSlot
              label={roEstimateFile.label}
              kinds={roEstimateFile.kinds}
              accept={roEstimateFile.accept}
              required
              items={uploads.items("roEstimate")}
              onPick={(files) => uploads.pick("roEstimate", files)}
              onRemove={(id) => uploads.remove("roEstimate", id)}
              onRetry={(id) => uploads.retry("roEstimate", id)}
              testid="booking-ro-estimate"
            />
          )}

          {isLastStep && !creating && (
            <div className="flex flex-col gap-2" data-testid="booking-review">
              <p className={NOTE}>Check the details, then create the group. The customer and your team are invited once it&apos;s ready.</p>
              <ReviewList
                rows={[
                  ["Vehicle", f.group_name.trim()],
                  ["Customer", `${f.customer_name.trim()} · +91${f.customer_number}`],
                  ["Workshop", f.workshop_location],
                  [roleLabel("sa"), f.service_advisor_name || f.service_advisor_number],
                  ["Promised", `${f.delivery_date} ${f.delivery_time}`],
                  ["RO Card", roFile("roCard")],
                  ["RO Estimate", roFile("roEstimate")],
                ]}
              />
            </div>
          )}

          {isLastStep && creating && <BookingProgress flow={flow} />}
        </div>

        {!creating && (
          <WizardFoot>
            {step > 0 ? <Btn onClick={back}>Back</Btn> : <span />}
            <Btn
              variant="pri"
              icon={isLastStep && !uploads.busy ? "wa" : undefined}
              onClick={isLastStep ? submit : next}
              disabled={!canNext()}
              data-testid={isLastStep ? "submit-create-group" : "booking-next"}
            >
              {uploads.busy ? (
                <>
                  <Loader2 className="animate-spin" size={15} /> Uploading…
                </>
              ) : (
                nextLabel()
              )}
            </Btn>
          </WizardFoot>
        )}
      </section>
    </BookingLayout>
  );
}
